/**
 * shared/collision/types.ts — collision contract (Q3-style box traces).
 *
 * OWNER: integration (contract); implementation in shared/collision/* is the COLLISION agent's.
 * Space: CoD Z-up inches. NOTE: collision.bin vertices are in three.js space; the loader converts.
 *
 * Contents / surface flags are the values actually present in the Hijacked clipMap
 * (game/public/assets/maps/mp_hijacked/collision.json `contents`, research/map-hijacked.md).
 */
import type { Vec3, ReadonlyVec3 } from '../math';

// ---- contents flags (collision.json "contents") --------------------------------------------
export const CONTENTS = {
  SOLID: 0x1,
  FOLIAGE: 0x2,
  NONCOLLIDING: 0x4,
  GLASS: 0x10, //          *glass* materials
  WATER: 0x20, //          water_ocean_*  (NOT solid)
  CANSHOOTCLIP: 0x40,
  MISSILECLIP: 0x80, //    clip_missile
  ITEM: 0x100,
  VEHICLECLIP: 0x200,
  ITEMCLIP: 0x400,
  SKY: 0x800, //           sky_day
  AI_NOSIGHT: 0x1000,
  CLIPSHOT: 0x2000,
  MOVER: 0x4000,
  PLAYERCLIP: 0x10000, //  clip_player
  MONSTERCLIP: 0x20000, // clip_ai
  TELEPORTER: 0x40000,
  JUMPPAD: 0x80000,
  CLUSTERPORTAL: 0x100000,
  DONOTENTER: 0x200000,
  DONOTENTER_LARGE: 0x400000,
  MANTLE: 0x1000000, //    mantle_on / mantle_over
  /** Player hit/clip volumes (IW3 CONTENTS_BODY; unused by map data so it cannot collide). */
  BODY: 0x2000000,
  DETAIL: 0x8000000,
  STRUCTURAL: 0x10000000,
  TRANSPARENT: 0x20000000,
  TRIGGER: 0x40000000,
  NODROP: 0x80000000,
} as const;

// ---- surface flags (collision.json "surfaceFlags") -----------------------------------------
export const SURF = {
  SLICK: 0x2,
  SKY: 0x4,
  LADDER: 0x8, // ESTIMATE (8 thin playerclip brushes on Hijacked carry it)
  NOIMPACT: 0x10,
  NOMARKS: 0x20,
  NODRAW: 0x80,
  NOLIGHTMAP: 0x400,
  NOSTEPS: 0x2000,
  NONSOLID: 0x4000,
  NODLIGHT: 0x20000,
  NOCASTSHADOW: 0x40000,
  MANTLE_ON: 0x4000000,
  MANTLE_OVER: 0x8000000,
} as const;

/** surfaceType = (surfaceFlags >> 20) & 31; names index footstep/impact/bounce aliases. */
export const SURFACE_TYPES: readonly string[] = [
  'default', 'bark', 'brick', 'carpet', 'cloth', 'concrete', 'dirt', 'flesh', 'foliage', 'glass', 'grass',
  'gravel', 'ice', 'metal', 'mud', 'paper', 'plaster', 'rock', 'sand', 'snow', 'water', 'wood', 'asphalt',
  'ceramic', 'plastic', 'rubber', 'cushion', 'fruit', 'paintedmetal', 'player', 'tallgrass', 'riotshield',
];
export const SURFACE_TYPE_FLESH = 7;
export function surfaceTypeFromFlags(surfaceFlags: number): number {
  return (surfaceFlags >>> 20) & 31;
}

// ---- masks (ESTIMATE: exact T6 masks not decompiled; see collision.json playerMaskNote) ------
/** Player movement: SOLID | GLASS | PLAYERCLIP (+ BODY so players block each other). */
export const MASK_PLAYERSOLID = CONTENTS.SOLID | CONTENTS.GLASS | CONTENTS.PLAYERCLIP | CONTENTS.BODY;
/** Missiles (bolt, knife, hatchet): world solids + missile clip + glass + bodies. */
export const MASK_MISSILESHOT = CONTENTS.SOLID | CONTENTS.GLASS | CONTENTS.MISSILECLIP | CONTENTS.BODY;
/** Melee / line of sight style traces. */
export const MASK_SHOT = CONTENTS.SOLID | CONTENTS.GLASS | CONTENTS.CANSHOOTCLIP | CONTENTS.BODY;
/** Visibility (bots, spawn LOS): opaque world only. */
export const MASK_OPAQUE = CONTENTS.SOLID;
export const MASK_WATER = CONTENTS.WATER;
/** World-only player clip (no bodies): navmesh bake, spawn floor checks. */
export const MASK_PLAYERSOLID_WORLD = CONTENTS.SOLID | CONTENTS.GLASS | CONTENTS.PLAYERCLIP;

/** Q3 SURFACE_CLIP_EPSILON: traces stop this far in front of the hit plane. */
export const SURFACE_CLIP_EPSILON = 0.125;

// ---- trace ---------------------------------------------------------------------------------
export interface TraceResult {
  /** 0..1 portion of the move completed (1 = no hit). */
  fraction: number;
  /** Final position (start + fraction * (end - start)). */
  endpos: Vec3;
  /** Hit plane normal (valid when fraction < 1). */
  normal: Vec3;
  /** Hit plane distance (normal . p = dist). */
  planeDist: number;
  /** Start position was inside a solid. */
  startsolid: boolean;
  /** Whole move was inside a solid (fraction 0). */
  allsolid: boolean;
  /** Contents of the hit surface/brush. */
  contents: number;
  /** Surface flags of the hit surface. */
  surfaceFlags: number;
  /** (surfaceFlags >> 20) & 31, see SURFACE_TYPES. */
  surfaceType: number;
  /** ENTITYNUM_WORLD, ENTITYNUM_NONE (no hit), or the clientNum / entity number hit. */
  entityNum: number;
}

export function createTraceResult(): TraceResult {
  return {
    fraction: 1, endpos: [0, 0, 0], normal: [0, 0, 0], planeDist: 0, startsolid: false, allsolid: false,
    contents: 0, surfaceFlags: 0, surfaceType: 0, entityNum: 0x3ff,
  };
}

/**
 * The static map. Implementations: BoxWorld (tests/placeholder, this dir) and the real
 * Hijacked collision (collision agent: loads collision.bin, BVH over triangles/brushes).
 * Must be deterministic and identical in Node, Worker and browser.
 */
export interface CollisionWorld {
  readonly name: string;
  /** World AABB of player-relevant geometry (POS_SCALE / quantization sanity). */
  readonly bounds: { mins: Vec3; maxs: Vec3 };
  /**
   * Sweep an AABB (mins/maxs relative to the origin) from start to end. Point trace when
   * mins = maxs = 0. Only geometry whose contents & contentMask != 0 blocks.
   * `passEntityNum` is accepted for signature parity with entity traces (ignored by the world).
   * Writes into and returns `out`.
   */
  trace(
    out: TraceResult, start: ReadonlyVec3, end: ReadonlyVec3, mins: ReadonlyVec3, maxs: ReadonlyVec3,
    contentMask: number, passEntityNum?: number,
  ): TraceResult;
  /** OR of the contents of everything containing point p, masked by contentMask. */
  pointContents(p: ReadonlyVec3, contentMask: number): number;
}

/** A dynamic box (player hull, pickup trigger) for entity traces. Absolute bounds. */
export interface ClipEntity {
  entityNum: number;
  absMins: Vec3;
  absMaxs: Vec3;
  contents: number;
  /** Entities owned by passEntityNum (e.g. own missiles) are skipped too. */
  ownerNum?: number;
}

/** Trace function handed to pmove/missiles: world + dynamic entities (built by the sim). */
export type TraceFn = (
  out: TraceResult, start: ReadonlyVec3, end: ReadonlyVec3, mins: ReadonlyVec3, maxs: ReadonlyVec3,
  contentMask: number, passEntityNum: number,
) => TraceResult;
